import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { find, get } from 'lodash-es'

import {
  SORT_FILTER_TRAIT_DATERANGE,
  SORT_FILTER_TRAIT_FULLTEXT,
  SORT_FILTER_TRAIT_MULTIVALUE,
  SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN,
  SORT_FILTER_TRAIT_UNRESTRICTED_VALUE,
  SORT_FILTER_TRAIT_VALUE,
} from '../../constants'
import { SortFilterTraitFilter } from '../../types/sort-filter-trait'
import {
  DateRangeFilterValue,
  FilterDefinitionWithComponent,
  FilterDefinitionWithoutComponent,
  FullTextFilterDefinition,
  GetFilterValueFromStoreParams,
  MantineDateRange,
} from './use-debounced-filter-value'

export const isValidColumn = <T>(
  possibleColumn: ColumnDef<T, any> | null,
): possibleColumn is ColumnDef<T, any> => {
  return possibleColumn !== null
}

export const isValidString = (
  potentialString: string | unknown,
): potentialString is string => {
  return Boolean(potentialString) && typeof potentialString === 'string'
}

export const isValidNumber = (
  potentialNumber: number | unknown,
): potentialNumber is number => {
  return typeof potentialNumber === 'number' && !isNaN(potentialNumber)
}
export const DEFAULT_DATEN_RANGE_FILTER: DateRangeFilterValue = {
  startDate: dayjs().subtract(2, 'weeks').toISOString(),
  endDate: dayjs().toISOString(),
}

export const dateRangeToMantine = (
  value: DateRangeFilterValue,
): [Date | null, Date | null] => [
  value.startDate ? new Date(value.startDate) : null,
  value.endDate ? new Date(value.endDate) : null,
]

export const mantineToDateRange = (
  value: MantineDateRange,
): DateRangeFilterValue => ({
  startDate: value[0] ? dayjs(value[0]).toISOString() : null,
  endDate: value[1] ? dayjs(value[1]).toISOString() : null,
})

export const isValidDateRangeFilter = (
  filterValue: DateRangeFilterValue | unknown,
): filterValue is DateRangeFilterValue => {
  if (!get(filterValue, 'startDate') || !get(filterValue, 'endDate')) {
    return false
  }

  const validStartDate = dayjs(get(filterValue, 'startDate')).isValid()
  const validEndDate = dayjs(get(filterValue, 'endDate')).isValid()

  return validStartDate && validEndDate
}

export const isValidMantineDateRange = (
  potentialMantineDateRange: MantineDateRange | unknown,
): potentialMantineDateRange is MantineDateRange => {
  return (
    Array.isArray(potentialMantineDateRange) &&
    typeof get(potentialMantineDateRange[0], 'getMonth') === 'function' &&
    typeof get(potentialMantineDateRange[1], 'getMonth') === 'function'
  )
}

export const getFulltextFilterDefinition = ({
  filterDefinitions,
}: {
  filterDefinitions: FilterDefinitionWithComponent[]
}): FullTextFilterDefinition =>
  find(
    filterDefinitions,
    (fd) => 'type' in fd && fd.type === 'fulltext',
  ) as FullTextFilterDefinition

export const getTextFilterValueFromSortFilter = ({
  activeFilters,
  filterField,
}: GetFilterValueFromStoreParams): string => {
  const textFilter = activeFilters.find((af) => af.field === filterField)

  return typeof textFilter?.value === 'string' ? textFilter.value : ''
}

export const getMultiValueFilterFromStore = ({
  activeFilters,
  filterField,
}: GetFilterValueFromStoreParams): string[] => {
  const multiValueFilter = activeFilters.find((af) => af.field === filterField)

  if (!multiValueFilter || !Array.isArray(multiValueFilter?.value)) {
    return []
  }

  const storeFilterToStringArr = multiValueFilter.value
    .map((value) => {
      return typeof value === 'number'
        ? value.toString()
        : typeof value === 'string'
        ? value
        : undefined
    })
    .filter(isValidString)

  return storeFilterToStringArr
}

export const getDateRangeFilterValueFromStoreFilter = ({
  activeFilters,
  filterField,
}: GetFilterValueFromStoreParams): DateRangeFilterValue => {
  const dateRangeFilter = find(
    activeFilters,
    (filter) => filter.field === filterField,
  )?.value ?? { startDate: null, endDate: null }

  return isValidDateRangeFilter(dateRangeFilter)
    ? dateRangeFilter
    : { startDate: null, endDate: null }
}

export const arraySlicer = (array: string[], lastSeparator: string): string => {
  if (array.length === 1) {
    return array[0]
  }
  return array.slice(0, -1).join(', ') + ` ${lastSeparator} ` + array.slice(-1)
}

export const getResetValue = ({
  filterDefinition,
}: {
  filterDefinition: FilterDefinitionWithoutComponent
}): string | [] | null | DateRangeFilterValue => {
  const { type } = filterDefinition

  if (type === SORT_FILTER_TRAIT_FULLTEXT || type === SORT_FILTER_TRAIT_VALUE) {
    return ''
  }

  if (type === SORT_FILTER_TRAIT_MULTIVALUE) {
    return []
  }

  if (type === SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN) {
    return SORT_FILTER_TRAIT_UNRESTRICTED_VALUE
  }

  if (type === SORT_FILTER_TRAIT_DATERANGE) {
    return { startDate: null, endDate: null }
  }

  return null
}

export const getInitialValue = <T>({
  filterDefinition,
  activeFilters,
}: {
  filterDefinition: FilterDefinitionWithComponent
  activeFilters: SortFilterTraitFilter[]
}): T => {
  const { type } = filterDefinition

  if (type === SORT_FILTER_TRAIT_FULLTEXT) {
    return getTextFilterValueFromSortFilter({
      activeFilters,
      filterField: SORT_FILTER_TRAIT_FULLTEXT,
    }) as T
  }

  if (
    type === SORT_FILTER_TRAIT_VALUE ||
    type === SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN
  ) {
    return getTextFilterValueFromSortFilter({
      activeFilters,
      filterField: filterDefinition.name,
    }) as T
  }

  if (type === SORT_FILTER_TRAIT_MULTIVALUE) {
    return getMultiValueFilterFromStore({
      activeFilters,
      filterField: filterDefinition.name,
    }) as T
  }

  if (type === SORT_FILTER_TRAIT_DATERANGE) {
    return dateRangeToMantine(
      getDateRangeFilterValueFromStoreFilter({
        activeFilters,
        filterField: filterDefinition.name,
      }),
    ) as T
  }

  return undefined as T
}
